/**
 * Route table and the app shell.
 *
 * Two surfaces, and the split at the top of `renderRoute` is load-bearing:
 * `/s/*` is the expert's, and it renders **before the auth gate** because they
 * have no Mauvely account and must never be sent to a sign-in page. Review
 * does the same for `/r/*`, and Cloud for `/s/*`.
 */

import { useEffect, useRef } from 'react';
import type React from 'react';
import { matchRoute, useRouter } from './router';
import { AppProvider, useApp } from './state';
import { Wordmark } from './components/Wordmark';
import { TopNav } from './components/TopNav';
import { Intake } from './pages/Intake';
import { Submissions } from './pages/Submissions';
import { Submission } from './pages/Submission';
import { NewProject } from './pages/NewProject';
import { ds } from './ds';
import { LAYOUT, RADIUS, display, mono, signInHref } from './theme';

/**
 * Which theme each surface wears. Set on the root element rather than chosen
 * by the reader — see theme.ts for why Scope has no toggle.
 */
function useSurfaceTheme(surface: 'designer' | 'intake') {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', surface === 'designer' ? 'dark' : 'light');
  }, [surface]);
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        // The app ground, not the site's: these are the app's own windows with
        // one message in them, and the ground has to be the thing that does not
        // change when the session lands.
        background: 'var(--app-ground)',
        textAlign: 'center'
      }}
    >
      {children}
    </div>
  );
}

/**
 * The route cross-fade.
 *
 * A wrapper that replays a CSS animation, not a `key` on the outlet. Keying
 * would remount the page under it, and the submission list and a submission are
 * different components anyway — what a key would break is a return to a page
 * that had state. Removing the class, reading a layout property to flush the
 * removal, and adding it back is the only way to restart an animation on an
 * element that stays where it is.
 */
function PageFade({ path, children }: { path: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove('mv-page');
    void el.offsetWidth;
    el.classList.add('mv-page');
  }, [path]);

  return (
    <div ref={ref} className="mv-page">
      {children}
    </div>
  );
}

function SignedOut() {
  useSurfaceTheme('designer');
  useEffect(() => {
    window.location.href = signInHref(window.location.href);
  }, []);
  return (
    <Centered>
      <Wordmark reversed />
      <p style={mono}>Taking you to sign in</p>
    </Centered>
  );
}

function SecondFactor() {
  useSurfaceTheme('designer');
  const { Button } = ds();
  return (
    <Centered>
      <Wordmark reversed />
      <h1 style={display(28)}>Finish signing in.</h1>
      <p style={{ fontSize: 15, color: 'var(--text-muted)', maxWidth: 420 }}>
        Your account uses two-factor authentication.
      </p>
      <Button
        variant="primary"
        size="md"
        onClick={() => {
          // /signin would mint another session in the same condition and loop.
          const next = encodeURIComponent(window.location.href);
          window.location.href = `${new URL(signInHref('/')).origin}/signin/two-factor?next=${next}`;
        }}
      >
        Continue
      </Button>
    </Centered>
  );
}

function Unreachable({ onRetry }: { onRetry: () => void }) {
  useSurfaceTheme('designer');
  const { Button } = ds();
  return (
    <Centered>
      <Wordmark reversed />
      <h1 style={display(28)}>Scope is not answering.</h1>
      <Button variant="primary" size="md" onClick={onRetry}>
        Try again
      </Button>
    </Centered>
  );
}

function NotReady() {
  useSurfaceTheme('designer');
  const { notReady } = useApp();
  return (
    <Centered>
      <Wordmark reversed />
      <h1 style={display(28)}>This deployment is not finished.</h1>
      <p style={{ fontSize: 15, color: 'var(--text-muted)', maxWidth: 520 }}>{notReady?.message}</p>
      {notReady?.missingTables?.length ? (
        <p style={{ ...mono, fontSize: 10 }}>Missing · {notReady.missingTables.join(' · ')}</p>
      ) : null}
      {notReady?.hint && (
        <p style={{ fontSize: 13.5, color: 'var(--text-faint)', maxWidth: 520 }}>{notReady.hint}</p>
      )}
    </Centered>
  );
}

/**
 * The designer's side, behind the gate.
 *
 * **Tinted chrome on a panel layout**, decided 2026-09-09. The window ground is
 * violet (`--app-ground`); the page is a neutral panel (`--app-panel`) at
 * `RADIUS.appPanel` with `LAYOUT.gutter` of ground showing around it; the top
 * bar sits on that ground with nothing under it. Scope has no nav rail — the
 * mockup's three items were one built item and two that open nothing — so there
 * is no chrome panel here and no marker to slide.
 *
 * The gutter above the bar is the bar's own `paddingTop`, not this container's:
 * a sticky element cannot stop below its scroll container's padding, so with the
 * padding here the panel would scroll up through a transparent band above the
 * bar.
 */
function Designer() {
  useSurfaceTheme('designer');
  const { path } = useRouter();

  const project = matchRoute('/p/:id', path);
  const G = LAYOUT.gutter;

  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        background: 'var(--app-ground)',
        display: 'flex',
        flexDirection: 'column',
        gap: G,
        padding: `0 ${G}px ${G}px`
      }}
    >
      <TopNav />
      <main
        style={{
          flex: 1,
          minWidth: 0,
          background: 'var(--app-panel)',
          borderRadius: RADIUS.appPanel
        }}
      >
        <PageFade path={path}>
          {path === '/new' ? (
            <NewProject />
          ) : project ? (
            <Submission projectId={project.id!} />
          ) : (
            <Submissions />
          )}
        </PageFade>
      </main>
    </div>
  );
}

function Routes() {
  const { status, refresh } = useApp();

  if (status === 'loading') {
    return (
      <Centered>
        <p style={mono}>Loading</p>
      </Centered>
    );
  }
  if (status === 'signedOut') return <SignedOut />;
  if (status === 'secondFactor') return <SecondFactor />;
  if (status === 'notReady') return <NotReady />;
  if (status === 'unreachable') return <Unreachable onRetry={() => void refresh()} />;

  return <Designer />;
}

function IntakeSurface({ token }: { token: string }) {
  useSurfaceTheme('intake');
  return <Intake token={token} />;
}

export function App() {
  const { path } = useRouter();

  /**
   * The expert's surface is chosen before the provider mounts, deliberately.
   *
   * Rendering it inside `AppProvider` worked, because the route is checked
   * before any status branch — but the provider still fetched `/api/scope/me`
   * on the way past, so every intake opened with a 401 in the console of
   * someone who has no account and never will. A page that must not consult an
   * account should not be inside the thing that consults it.
   */
  const intake = matchRoute('/s/:slug/:token', path);
  if (intake) return <IntakeSurface token={intake.token!} />;

  return (
    <AppProvider>
      <Routes />
    </AppProvider>
  );
}
